import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Loader2, ChevronDown, ChevronUp, Settings2, Pause, Play, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  PartySubscription,
  SubscriptionStatus,
  SUBSCRIPTION_STATUSES,
  BILLING_TYPES,
  RETURN_FREQUENCIES,
} from '@/app/types/compliance';
import {
  getPartySubscriptionsForParty,
  updatePartySubscription,
  deletePartySubscription,
} from '@/app/services/partySubscriptionService';
import SubscriptionForm from './SubscriptionForm';
import { format, parseISO } from 'date-fns';

interface PartySubscriptionsSectionProps {
  partyId: number;
  partyName?: string;
}

const PartySubscriptionsSection: React.FC<PartySubscriptionsSectionProps> = ({
  partyId,
  partyName,
}) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<PartySubscription | null>(null);
  const [deletingSubscription, setDeletingSubscription] = useState<PartySubscription | null>(null);

  // Fetch subscriptions for this party
  const {
    data: subscriptionsResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['party-subscriptions', partyId],
    queryFn: () => getPartySubscriptionsForParty(partyId),
    enabled: !!partyId,
  });

  const subscriptions = subscriptionsResponse?.items ?? [];
  const activeCount = subscriptions.filter((s) => s.status === 'active').length;

  // Update mutation (for pause/resume)
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { status: SubscriptionStatus } }) =>
      updatePartySubscription(id, data),
    onSuccess: () => {
      toast.success('Subscription updated');
      queryClient.invalidateQueries({ queryKey: ['party-subscriptions', partyId] });
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePartySubscription(id),
    onSuccess: () => {
      toast.success('Subscription cancelled');
      queryClient.invalidateQueries({ queryKey: ['party-subscriptions', partyId] });
      setDeletingSubscription(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const handleAddClick = () => {
    setEditingSubscription(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (subscription: PartySubscription) => {
    setEditingSubscription(subscription);
    setIsFormOpen(true);
  };

  const handlePauseResume = (subscription: PartySubscription) => {
    const newStatus: SubscriptionStatus =
      subscription.status === 'active' ? 'paused' : 'active';
    updateMutation.mutate({
      id: subscription.id,
      data: { status: newStatus },
    });
  };

  const handleDelete = (subscription: PartySubscription) => {
    setDeletingSubscription(subscription);
  };

  const confirmDelete = () => {
    if (deletingSubscription) {
      deleteMutation.mutate(deletingSubscription.id);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadgeVariant = (
    status: SubscriptionStatus
  ): 'default' | 'secondary' | 'destructive' => {
    switch (status) {
      case 'active':
        return 'default';
      case 'paused':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getBillingTypeLabel = (type: string): string => {
    const found = BILLING_TYPES.find((b) => b.value === type);
    return found?.label || type;
  };

  const getStatusLabel = (status: string): string => {
    const found = SUBSCRIPTION_STATUSES.find((s) => s.value === status);
    return found?.label || status;
  };

  const getFrequencyLabel = (frequency: string | null): string => {
    if (!frequency) return '';
    const found = RETURN_FREQUENCIES.find((f) => f.value === frequency);
    return found?.label || frequency;
  };

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card>
          <CardHeader className="pb-3">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Service Subscriptions
                    {activeCount > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {activeCount} active
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Services subscribed to by this client
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddClick();
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="pt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : isError ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    Failed to load subscriptions. Please try again.
                  </AlertDescription>
                </Alert>
              ) : subscriptions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No services subscribed yet.</p>
                  <p className="text-sm mt-1">
                    Click "Add" to subscribe this client to services.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {subscriptions.map((subscription) => (
                    <div
                      key={subscription.id}
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {subscription.item_name || subscription.item_code || `Item #${subscription.item_id}`}
                            </span>
                            {subscription.compliance_service_code && (
                              <span className="text-xs text-muted-foreground">
                                ({subscription.compliance_service_code})
                              </span>
                            )}
                            <Badge variant={getStatusBadgeVariant(subscription.status)}>
                              {getStatusLabel(subscription.status)}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground space-x-4">
                            <span>
                              Started: {formatDate(subscription.start_date)}
                            </span>
                            <span>
                              Billing: {getBillingTypeLabel(subscription.billing_type)}
                            </span>
                            <span>Fee: Rs.{subscription.fee_amount}</span>
                            {subscription.assigned_user_name && (
                              <span>Assigned: {subscription.assigned_user_name}</span>
                            )}
                          </div>
                          {(subscription.frequency || subscription.due_day_of_month) && (
                            <div className="text-xs text-muted-foreground">
                              {subscription.frequency && (
                                <span className="mr-3">
                                  Frequency: {getFrequencyLabel(subscription.frequency)}
                                </span>
                              )}
                              {subscription.due_day_of_month && (
                                <span>
                                  Due day: {subscription.due_day_of_month}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClick(subscription)}
                            title="Edit subscription"
                          >
                            <Settings2 className="h-4 w-4" />
                          </Button>
                          {subscription.status !== 'cancelled' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePauseResume(subscription)}
                              title={
                                subscription.status === 'active' ? 'Pause' : 'Resume'
                              }
                              disabled={updateMutation.isPending}
                            >
                              {subscription.status === 'active' ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(subscription)}
                            title="Cancel subscription"
                            className="text-destructive hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Subscription Form Dialog */}
      <SubscriptionForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        partyId={partyId}
        partyName={partyName}
        subscription={editingSubscription}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['party-subscriptions', partyId] });
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingSubscription}
        onOpenChange={(open) => !open && setDeletingSubscription(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the subscription to{' '}
              <strong>{deletingSubscription?.item_name || deletingSubscription?.compliance_service_name}</strong>? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Cancel Subscription'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PartySubscriptionsSection;
